import {
  createFrontendModule,
  type FrontendModule,
} from '@backstage/frontend-plugin-api';
import { EntityContentLayoutBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { z } from 'zod/v4';

/**
 * One entity content layout per kind, each reproducing the row composition the
 * legacy `EntityPage.tsx` built with MUI `Grid`. That file no longer exists —
 * it was deleted once these layouts replaced it; `git log --diff-filter=D --
 * packages/app/src/components/catalog/EntityPage.tsx` is the way back to it.
 *
 * `entity-content:catalog/overview` picks the first layout whose filter matches
 * the entity and falls back to `DefaultEntityContentLayout` (the two column
 * content/info split) for everything else — the kinds the TeamSystem model does
 * not use (`System`, `Domain`, `Template`, …) keep the stock layout unless a
 * layout is added for them here.
 *
 * The rows are not hardcoded: they come from `app.extensions` in
 * `app-config.yaml`, so the composition can be changed without a rebuild. The
 * `filter` of each layout is part of the blueprint's own config schema, so it
 * can be widened from config too — pointing the `component` layout at
 * `{ kind: { $in: [component, system] } }` needs no code change.
 */
const createKindLayout = (options: { name: string; kind: string }) =>
  EntityContentLayoutBlueprint.makeWithOverrides({
    name: options.name,
    configSchema: {
      rows: z.array(z.array(z.string())).default([]),
      appendUnlisted: z.boolean().default(true),
    },
    factory(originalFactory, { config }) {
      return originalFactory({
        filter: { kind: options.kind },
        loader: async () => {
          const { GridLayout } = await import('./GridLayout');
          return props => (
            <GridLayout
              {...props}
              rows={config.rows}
              appendUnlisted={config.appendUnlisted}
            />
          );
        },
      });
    },
  });

const moduleLayout = createKindLayout({ name: 'module', kind: 'module' });
const componentLayout = createKindLayout({
  name: 'component',
  kind: 'component',
});
const apiLayout = createKindLayout({ name: 'api', kind: 'api' });
const groupLayout = createKindLayout({ name: 'group', kind: 'group' });
const userLayout = createKindLayout({ name: 'user', kind: 'user' });
const areaLayout = createKindLayout({ name: 'area', kind: 'area' });
const resourceLayout = createKindLayout({ name: 'resource', kind: 'resource' });

export const catalogLayoutModule: FrontendModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    moduleLayout,
    componentLayout,
    apiLayout,
    groupLayout,
    userLayout,
    areaLayout,
    resourceLayout,
  ],
});
